import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Package, X, AlertCircle, Clock, CheckCircle, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { useFuturesTrading, PositionHistoryEntry } from '../hooks/useFuturesTrading';
import { DatabaseFuturesPosition, MarketData } from '../hooks/useDatabase';
import { supabase } from '../lib/supabaseClient';
import { TickerData } from '../hooks/useBybitWebSocket';
import { usePrevious } from '../hooks/usePrevious';

interface FuturesMyOrdersProps {
  currentBtcPrice: number;
  futuresPositions: any[];
  marketData: MarketData[];
  onClosePosition: (positionId: string) => void;
  updateBalances?: (updates: { usdt_balance?: number }) => Promise<void>;
  openOrders?: any[];
  onCancelOrder?: (orderId: string) => Promise<boolean>;
  onCancelAllOrders?: () => Promise<boolean>;
  ticker?: TickerData | null;
  tickers?: Record<string, TickerData>;
}

interface FuturesOrder {
  id: string;
  user_id: string;
  symbol: string;
  type: 'limit' | 'market' | 'stop';
  side: 'buy' | 'sell';
  price: number | null;
  amount: number;
  leverage: number;
  status: 'open' | 'filled' | 'cancelled';
  created_at: string;
  filled_at: string | null;
  position_id: string | null;
}


const FuturesMyOrders: React.FC<FuturesMyOrdersProps> = ({
  currentBtcPrice,
  futuresPositions,
  marketData,
  onClosePosition,
  updateBalances,
  openOrders = [],
  onCancelOrder,
  onCancelAllOrders,
  ticker,
  tickers = {}
}) => {
  const { loadPositionHistory, positionHistory, loading, error } = useFuturesTrading();
  const [activeTab, setActiveTab] = useState('Positions');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const tabs = ['Positions', 'Open Orders', 'Position History'];
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [processingPositions, setProcessingPositions] = useState<Record<string, boolean>>({});
  
  // Use a ref to store previous prices for each symbol
  const previousPricesRef = useRef<Record<string, number>>({});

  // Debug logs
  console.log('FuturesMyOrders - Render with props:', {
    futuresPositions,
    ticker,
    tickers,
    marketData
  });

  // Log when tickers change
  useEffect(() => {
    console.log('Tickers updated:', tickers);
  }, [tickers]);

  // Update previous prices ref when tickers change
  useEffect(() => {
    if (tickers) {
      const newPrices: Record<string, number> = {};
      Object.entries(tickers).forEach(([symbol, tickerData]) => {
        if (tickerData && tickerData.price > 0) {
          newPrices[symbol] = tickerData.price;
        }
      });
      console.log('Updating previous prices ref:', newPrices);
      previousPricesRef.current = {...previousPricesRef.current, ...newPrices};
    }
  }, [tickers]);

  // Calculate real-time PnL and ROI for each position
  const positionsWithLiveData = futuresPositions.map(position => {
    // Initialize livePrice to 0 (will be populated from the best available source)
    let livePrice = 0;
    const symbol = position.symbol;
    
    // Debug log for each position
    console.log(`Position ${position.id} (${symbol}) - Current data:`, {
      originalPrice: position.currentPrice || position.current_price || 0,
      hasTickerData: !!tickers[symbol],
      tickerPrice: tickers[symbol]?.price,
      marketDataPrice: marketData.find(data => data.symbol === symbol)?.price
    });
    
    // 1. First priority: Use WebSocket ticker price if available and valid
    if (tickers && tickers[symbol] && typeof tickers[symbol].price === 'number' && tickers[symbol].price > 0) {
      livePrice = tickers[symbol].price;
      console.log(`Using WebSocket price for ${symbol}: ${livePrice}`);
    } 
    // 2. Second priority: Use market data price if available
    else if (marketData.find(data => data.symbol === symbol)?.price) {
      livePrice = marketData.find(data => data.symbol === symbol)!.price;
      console.log(`Using market data price for ${symbol}: ${livePrice}`);
    }
    // 3. Third priority: Fall back to position's current price
    else if (position.currentPrice || position.current_price) {
      livePrice = position.currentPrice || position.current_price || 0;
      console.log(`Using position's current price for ${symbol}: ${livePrice}`);
    }
    // 4. Last resort: Use entry price if nothing else is available
    else if (position.entryPrice || position.entry_price) {
      livePrice = position.entryPrice || position.entry_price || 0;
      console.log(`Using entry price as fallback for ${symbol}: ${livePrice}`);
    }
    
    // Get position properties with fallbacks for both naming conventions
    const entryPrice = position.entryPrice || position.entry_price || 0;
    const amount = position.amount || 0;
    const leverage = position.leverage || 1;
    const margin = position.margin || 0;
    const side = position.side || 'long';
    const liquidationPrice = position.liquidationPrice || position.liquidation_price || 0;
    
    // Calculate unrealized PnL based on live price
    let unrealizedPnl = 0;
    if (side === 'long') {
      unrealizedPnl = (livePrice - entryPrice) * amount * leverage;
    } else {
      unrealizedPnl = (entryPrice - livePrice) * amount * leverage;
    }
    
    // Calculate ROI
    const roi = margin > 0 ? (unrealizedPnl / margin) * 100 : 0;
    
    // Determine flash class based on price change
    let flashClass = '';
    const prevPrice = previousPricesRef.current[symbol];
    if (prevPrice && livePrice !== prevPrice) {
      flashClass = livePrice > prevPrice ? 'animate-flash-green' : 'animate-flash-red';
      console.log(`Price change for ${symbol}: ${prevPrice} -> ${livePrice}, flash: ${flashClass}`);
    }
    
    return {
      ...position,
      currentPrice: livePrice,
      entryPrice,
      amount,
      leverage,
      margin,
      side,
      liquidationPrice,
      unrealizedPnl,
      roi,
      flashClass
    };
  });

  // After calculating positions with live data, update previous prices ref
  useEffect(() => {
    if (positionsWithLiveData.length > 0) {
      const newPrices: Record<string, number> = {};
      positionsWithLiveData.forEach(position => {
        if (position.symbol && position.currentPrice > 0) {
          newPrices[position.symbol] = position.currentPrice;
        }
      });
      previousPricesRef.current = {...previousPricesRef.current, ...newPrices};
    }
  }, [positionsWithLiveData]);

  const totalUnrealizedPnl = positionsWithLiveData.reduce((sum, pos) => sum + (pos.unrealizedPnl || 0), 0);
  const totalMargin = positionsWithLiveData.reduce((sum, pos) => sum + (pos.margin || 0), 0);
  const runningPositions = positionsWithLiveData.length;

  // Load position history when tab changes to Position History
  useEffect(() => {
    if (activeTab === 'Position History') {
      loadPositionHistory();
    }
  }, [activeTab, loadPositionHistory]);

  // Update error message when error changes
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      
      // Clear error after 5 seconds
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleClosePosition = async (positionId: string) => {
    try {
      setProcessingPositions(prev => ({ ...prev, [positionId]: true }));
      const success = await onClosePosition(positionId);
      
      if (success) {
        setSuccessMessage('Position closed successfully');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to close position');
    } finally {
      setProcessingPositions(prev => ({ ...prev, [positionId]: false }));
    }
  };

  const closeAllPositions = async () => {
    setIsProcessingAll(true);
    try {
      for (const position of futuresPositions) {
        await handleClosePosition(position.id);
      }
    } catch (error) {
      console.error('Error closing all positions:', error);
      setErrorMessage('Failed to close all positions');
    } finally {
      setIsProcessingAll(false);
    }
  };

  // Handle cancel order
  const handleCancelOrder = async (orderId: string) => {
    if (!onCancelOrder) return;
    
    try {
      const success = await onCancelOrder(orderId);
      
      if (success) {
        setSuccessMessage('Order cancelled successfully');
      } else {
        throw new Error('Failed to cancel order');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to cancel order');
    }
  };

  // Handle cancel all orders
  const handleCancelAllOrders = async () => {
    if (!onCancelAllOrders) return;
    
    setIsProcessingAll(true);
    try {
      const success = await onCancelAllOrders();
      
      if (success) {
        setSuccessMessage('All orders cancelled successfully');
      } else {
        throw new Error('Failed to cancel orders');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to cancel orders');
    } finally {
      setIsProcessingAll(false);
    }
  };

  // Format duration from seconds to human-readable format
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
  };

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl h-full flex flex-col">
      {/* Status Messages */}
      {errorMessage && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400">{errorMessage}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
          <span className="text-green-400">{successMessage}</span>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-8">
          <div className="flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
                  activeTab === tab 
                    ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Assets Section */}
        <div className="bg-slate-900/30 rounded-xl p-6 min-w-96 border border-slate-600/30 shadow-lg">
          <h4 className="text-cyan-400 font-semibold mb-4 text-lg">Assets</h4>
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div>
              <span className="text-slate-400">Overall Balance:</span>
              <div className="text-white font-mono text-lg">${totalMargin.toFixed(4)} USD</div>
            </div>
            <div>
              <span className="text-slate-400">Available Balance:</span>
              <div className="text-white font-mono text-lg">0 USDT</div>
            </div>
            <div>
              <span className="text-slate-400">Unrealized Pnl:</span>
              <div className={`font-mono text-lg ${totalUnrealizedPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {totalUnrealizedPnl.toFixed(4)} USDT
              </div>
            </div>
            <div>
              <span className="text-slate-400">Running Position:</span>
              <div className="text-white font-mono text-lg">{runningPositions}</div>
            </div>
            <div>
              <span className="text-slate-400">Burning Position:</span>
              <div className="text-white font-mono text-lg">0</div>
            </div>
            <div>
              <span className="text-slate-400">Open Orders:</span>
              <div className="text-white font-mono text-lg">{openOrders.length}</div>
            </div>
          </div>
          <div className="flex gap-4 mt-6">
            <button 
              onClick={closeAllPositions}
              disabled={isProcessingAll || futuresPositions.length === 0 || loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/25 transform hover:scale-105 flex items-center justify-center gap-2"
            >
              {isProcessingAll ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                'Close All Positions'
              )}
            </button>
            <button 
              onClick={handleCancelAllOrders}
              disabled={isProcessingAll || openOrders.length === 0 || loading || !onCancelAllOrders}
              className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-red-500/25 transform hover:scale-105 flex items-center justify-center gap-2"
            >
              {isProcessingAll ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                'Cancel All Orders'
              )}
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'Positions' && (
        <div className="flex-1 flex flex-col">
          <div className="flex justify-between text-xs text-slate-400 mb-4 font-medium">
            <span>Symbol</span>
            <span>Size</span>
            <span>Side</span>
            <span>Entry Price</span>
            <span>Current Price</span>
            <span>Liq. Price</span>
            <span>Margin</span>
            <span>PNL (ROI)</span>
            <span></span>
          </div>

          {positionsWithLiveData.length > 0 ? (
            <div className="space-y-3 flex-1 overflow-auto">
              {positionsWithLiveData.map((position) => (
                <div key={position.id} className="flex items-center justify-between bg-slate-900/30 px-6 py-4 rounded-xl hover:bg-slate-900/50 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 shadow-lg">
                  <span className="text-white font-medium">{position.symbol}</span>
                  <span className={`font-mono ${position.side === 'long' ? 'text-emerald-400' : 'text-red-400'}`}>
                    {(position.amount || 0).toFixed(4)}
                  </span>
                  <span className={`text-xs font-bold px-3 py-1 rounded-lg ${
                    position.side === 'long' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                  }`}>
                    {(position.side || 'long').toUpperCase()}
                  </span>
                  <span className="text-slate-300 font-mono">{(position.entryPrice || 0).toFixed(4)}</span>
                  <span className={`text-slate-300 font-mono ${position.flashClass}`}>
                    {(position.currentPrice || 0).toFixed(4)}
                  </span>
                  <span className="text-red-400 font-mono">{(position.liquidationPrice || 0).toFixed(2)}</span>
                  <span className="text-slate-300 font-mono">{(position.margin || 0).toFixed(2)}</span>
                  <div className="text-right">
                    <div className={`font-mono ${(position.unrealizedPnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(position.unrealizedPnl || 0).toFixed(4)}
                    </div>
                    <div className={`text-xs ${(position.roi || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      ({(position.roi || 0).toFixed(2)}%)
                    </div>
                  </div>
                  <button 
                    onClick={() => handleClosePosition(position.id)}
                    disabled={loading || processingPositions[position.id]}
                    className="text-slate-400 hover:text-red-400 transition-colors p-2 hover:bg-red-400/10 rounded-lg disabled:opacity-50"
                  >
                    {processingPositions[position.id] ? (
                      <div className="w-4 h-4 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                    ) : (
                      <X size={16} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 flex-1">
              <Package size={64} className="mb-6 opacity-30" />
              <p className="text-lg">No positions found</p>
              <p className="text-sm text-slate-600 mt-2">Your futures positions will appear here</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'Open Orders' && (
        <div className="flex-1 flex flex-col">
          <div className="flex justify-between text-xs text-slate-400 mb-4 font-medium">
            <span>Date</span>
            <span>Pair</span>
            <span>Type</span>
            <span>Side</span>
            <span>Price</span>
            <span>Amount</span>
            <span>Leverage</span>
            <span>Status</span>
            <span></span>
          </div>
          
          {loadingOrders ? (
            <div className="flex justify-center py-8 flex-1">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : openOrders.length > 0 ? (
            <div className="space-y-3 flex-1 overflow-auto">
              {openOrders.map((order) => (
                <div key={order.id} className="flex items-center justify-between bg-slate-900/30 px-6 py-4 rounded-xl hover:bg-slate-900/50 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 shadow-lg">
                  <span className="text-slate-300 text-sm font-mono">
                    {new Date(order.createdAt || order.created_at).toLocaleString()}
                  </span>
                  <span className="text-white font-medium">{order.symbol}</span>
                  <span className="text-slate-300 text-sm capitalize">{order.type}</span>
                  <span className={`text-xs font-bold px-3 py-1 rounded-lg ${
                    order.side === 'buy' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                  }`}>
                    {order.side.toUpperCase()}
                  </span>
                  <span className="text-slate-300 font-mono">{order.price?.toFixed(4) || 'Market'}</span>
                  <span className="text-slate-300 font-mono">{(order.amount || 0).toFixed(4)}</span>
                  <span className="text-orange-400 font-mono">{order.leverage || 1}x</span>
                  <span className="text-blue-400 text-sm">Open</span>
                  <button 
                    onClick={() => onCancelOrder && handleCancelOrder(order.id)}
                    disabled={loading || !onCancelOrder}
                    className="text-slate-400 hover:text-red-400 transition-colors p-2 hover:bg-red-400/10 rounded-lg disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                    ) : (
                      <X size={16} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 flex-1">
              <Package size={64} className="mb-6 opacity-30" />
              <p className="text-lg">No open orders found</p>
              <p className="text-sm text-slate-600 mt-2">Your pending orders will appear here</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'Position History' && (
        <div className="flex-1 flex flex-col">
          <div className="flex justify-between text-xs text-slate-400 mb-4 font-medium">
            <span>Date</span>
            <span>Symbol</span>
            <span>Side</span>
            <span>Entry Price</span>
            <span>Exit Price</span>
            <span>PnL</span>
            <span>ROI</span>
            <span>Duration</span>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-8 flex-1">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : positionHistory.length > 0 ? (
            <div className="space-y-3 flex-1 overflow-auto">
              {positionHistory.map((position) => (
                <div key={position.id} className="flex items-center justify-between bg-slate-900/30 px-6 py-4 rounded-xl hover:bg-slate-900/50 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 shadow-lg">
                  <span className="text-slate-300 text-sm font-mono">
                    {new Date(position.closeTime).toLocaleString()}
                  </span>
                  <span className="text-white font-medium">{position.symbol}</span>
                  <span className={`text-xs font-bold px-3 py-1 rounded-lg ${
                    position.side === 'long' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                  }`}>
                    {position.side.toUpperCase()}
                  </span>
                  <span className="text-slate-300 font-mono">{position.entryPrice.toFixed(4)}</span>
                  <span className="text-slate-300 font-mono">{position.exitPrice.toFixed(4)}</span>
                  <span className={`font-mono ${position.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {position.pnl.toFixed(4)}
                  </span>
                  <span className={`flex items-center ${position.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {position.roi >= 0 ? (
                      <TrendingUp size={14} className="mr-1" />
                    ) : (
                      <TrendingDown size={14} className="mr-1" />
                    )}
                    {position.roi.toFixed(2)}%
                  </span>
                  <span className="text-slate-300 flex items-center">
                    <Clock size={14} className="mr-1 text-slate-400" />
                    {formatDuration(position.durationSeconds)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 flex-1">
              <Package size={64} className="mb-6 opacity-30" />
              <p className="text-lg">No position history found</p>
              <p className="text-sm text-slate-600 mt-2">Your closed positions will appear here</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FuturesMyOrders;